import React, {useCallback, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
    Bar,
    Button,
    FontVariant,
    List,
    notificationToastSignal,
    Text,
    ThemeProvider,
    ToggleSwitch,
    withColorScheme,
} from '@pexip/components';
import type {PreviewStreamController} from '@pexip/media';
import {DeniedDevices} from '@pexip/media';
import {
    AudioMeter,
    type DeviceStatusInfo,
    DevicesList,
    SelfViewSettings,
    SettingsPanel,
    useDeviceStatusInfo,
    usePreviewAnalyzer,
    usePreviewControllerHandler,
    usePreviewErrorHandling,
} from '@pexip/media-components';
import {reverseDeviceId} from '@pexip/media-control';
import {noop} from '@pexip/utils';

import {TestId} from '../../test/testIds';
import {SettingsActionsRow} from '../components/SettingsActionsRow';
import {useConfig} from '../config';
import {useAudioOutput, useDevices, useMediaRequest} from '../hooks/userMedia';
import {
    usePreviewAudioInput,
    usePreviewAudioOutput,
    usePreviewStreamQuality,
    usePreviewVideoInput,
} from '../services/Media.service';

import {OutputAudioTester} from './OutputAudioTester.viewModel';

const SecondaryAudioMeter = withColorScheme(AudioMeter, 'light');

export const MediaControl: React.FC<{
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    controller: PreviewStreamController;
}> = ({close, controller}) => {
    const {t} = useTranslation();
    const devices = useDevices();
    const [isUserFacing] = useConfig('isUserFacing');
    const {
        handleSave,
        isSaving,
        previewMedia,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoInputChanged,
        videoMuted,
    } = usePreviewControllerHandler({
        controller,
        applyChanges: () => {
            videoInput.applyChanges();
            audioInput.applyChanges();
            audioOutput.applyChanges();
            setMirrorSelfview(previewMirror);
            return streamQuality.applyChanges();
        },
        close,
    });
    const requested = {
        audio: Boolean(
            controller.audioInput ??
            previewMedia?.getOriginalConstraints().audio,
        ),
        video: Boolean(
            controller.videoInput ??
            previewMedia?.getOriginalConstraints().video,
        ),
    };
    const deviceStatusInfo = useDeviceStatusInfo(status, requested);

    const {
        videoInputError,
        setVideoInputError,
        audioInputError,
        setAudioInputError,
    } = usePreviewErrorHandling(controller, status, requested);

    const audioInput = usePreviewAudioInput({
        error: audioInputError,
        controller,
        setError: setAudioInputError,
    });

    const videoInput = usePreviewVideoInput({
        error: videoInputError,
        controller,
        setError: setVideoInputError,
    });
    const mainAudioOutput = useAudioOutput();
    const audioOutput = usePreviewAudioOutput(devices, mainAudioOutput);

    const streamQuality = usePreviewStreamQuality();
    const request = useMediaRequest();
    const [allowChangeOfMediaDirection] = useConfig(
        'allowChangeOfMediaDirection',
    );
    const [mirrorSelfview, setMirrorSelfview] = useConfig('mirrorSelfview');
    const [previewMirror, setPreviewMirror] = useState(mirrorSelfview);
    const isMirrored = isUserFacing ? previewMirror : false;

    useEffect(
        () =>
            controller.onApplyChangesError(() =>
                notificationToastSignal.emit([
                    {
                        message: t(
                            'settings.video-and-sound-section.apply-changes-error',
                            'There was a problem applying the Video and sound changes',
                        ),
                        timeout: 0,
                        isClickable: true,
                        isDanger: true,
                        isInterrupt: true,
                        onDismiss: noop,
                    },
                ]),
            ),
        [controller, t],
    );

    const analyzer = usePreviewAnalyzer(previewMedia);

    const requestedAudio = requested.audio;
    const requestedVideo = requested.video;

    const handleAudioOutputChange = (deviceId: string) => {
        const device = reverseDeviceId(deviceId);
        const [selectedDevice] = devices.get(device);
        if (selectedDevice) {
            audioOutput.setPreview(selectedDevice);
        }
    };
    const retryAudioInput = useCallback(() => {
        setAudioInputError({title: ''});
        void controller.retryPreviewInput('audio');
    }, [controller, setAudioInputError]);
    const retryVideoInput = useCallback(() => {
        setVideoInputError({title: ''});
        void controller.retryPreviewInput('video');
    }, [controller, setVideoInputError]);

    const deviceList = (
        <DevicesList
            devices={devices}
            disableVideoInputSelector={
                (!allowChangeOfMediaDirection.video && !request.video) ||
                updatingPreviewVideo
            }
            disableAudioInputSelector={
                (!allowChangeOfMediaDirection.audio && !request.audio) ||
                updatingPreviewAudio
            }
            disableAudioOutputSelector={false}
            requestedAudio={requestedAudio}
            requestedVideo={requestedVideo}
            videoInputError={(requestedVideo && videoInputError) || undefined}
            audioInputError={(requestedAudio && audioInputError) || undefined}
            videoInput={videoInput.preview}
            audioInput={audioInput.preview}
            audioOutput={audioOutput.preview}
            onAudioInputChange={audioInput.setPreview}
            onAudioOutputChange={handleAudioOutputChange}
            onVideoInputChange={videoInput.setPreview}
            retryAudioInput={retryAudioInput}
            retryVideoInput={retryVideoInput}
            showTooltip
            isLoading={isSaving}
        />
    );

    const audioMeter = !!previewMedia?.audioInput && !!analyzer && (
        <SecondaryAudioMeter
            analyzer={analyzer}
            data-testid={TestId.AudioMeterSettings}
        />
    );

    const allowToSave =
        !isSaving &&
        /*
         * Respective device selection is disabled when requestedVideo or requestedAudio
         * is false, this should not hinder the user from saving other changes.
         */
        (!videoInput.error.title || !requestedVideo) &&
        (!audioInput.error.title || !requestedAudio) &&
        !updatingPreviewAudio &&
        !updatingPreviewVideo;

    const previewStream = previewMedia?.stream;
    const requestCameraPreview = useCallback(() => {
        if (deviceStatusInfo.video.retriable) {
            retryVideoInput();
            return;
        }
        if (controller.media?.videoInput) {
            if (
                controller.media.getVideoTracks().some(track => track.stopped)
            ) {
                controller.updatePreviewInput(controller.media.videoInput);
            } else {
                controller.media.muteVideo(false);
            }
        } else if (controller.media?.expectedVideoInput) {
            controller.updatePreviewInput(controller.media.expectedVideoInput);
        }
    }, [controller, deviceStatusInfo.video.retriable, retryVideoInput]);
    const stopCameraPreview = useCallback(() => {
        controller.media?.muteVideo(true);
    }, [controller]);

    let mergedDeviceStatusInfo: DeviceStatusInfo = {
        title: t(
            'settings.turn-on-preview-title',
            'Want to see your camera preview?',
        ),
        message: t(
            'settings.turn-on-preview-message',
            'To show you a preview the App needs to request access to your camera.',
        ),
    };
    const requestedErrorInfo =
        requestedVideo && deviceStatusInfo.video.error
            ? deviceStatusInfo.video
            : undefined;
    if (requestedErrorInfo) {
        mergedDeviceStatusInfo = requestedErrorInfo;
    } else if (videoInputError.title) {
        mergedDeviceStatusInfo = {
            title: videoInputError.title,
            message: videoInputError.description ?? '',
            error: DeniedDevices.Camera,
        };
    }

    return (
        <SettingsPanel
            scrollable
            data-testid={TestId.VideoAndSoundPanel}
            footerContent={
                <ThemeProvider colorScheme="light">
                    <Bar>
                        <Button
                            onClick={close}
                            variant="secondary"
                            size="medium"
                            modifier="fullWidth"
                            isDisabled={isSaving}
                            data-testid={TestId.ButtonSettingsCancel}
                        >
                            <Trans t={t} i18nKey="settings.cancel">
                                Cancel
                            </Trans>
                        </Button>
                        <Button
                            onClick={handleSave}
                            type="submit"
                            modifier="fullWidth"
                            className="ms-2"
                            isLoading={isSaving}
                            isDisabled={!allowToSave}
                            data-testid={TestId.ButtonSettingsSave}
                        >
                            <Trans t={t} i18nKey="settings.save">
                                Save
                            </Trans>
                        </Button>
                    </Bar>
                </ThemeProvider>
            }
        >
            <ThemeProvider colorScheme="light">
                <SelfViewSettings
                    deviceStatusInfo={mergedDeviceStatusInfo}
                    isDisabled={
                        (!allowChangeOfMediaDirection.video &&
                            !request.video) ||
                        (Boolean(
                            previewMedia
                                ?.getVideoTracks()
                                .some(track => track.suspended),
                        ) &&
                            !deviceStatusInfo.video.retriable)
                    }
                    isMirrored={isMirrored}
                    isVideoInputMuted={videoMuted || !!videoInputError.title}
                    localMediaStream={previewStream}
                    requestCameraPreview={requestCameraPreview}
                    showPreviewLabel={
                        !(videoMuted || !!videoInputError.title) &&
                        videoInputChanged
                    }
                    stopCameraPreview={stopCameraPreview}
                    updatingPreviewAudio={updatingPreviewAudio}
                    updatingPreviewVideo={updatingPreviewVideo}
                >
                    {audioMeter}
                    <SettingsActionsRow
                        title={
                            <Text fontVariant={FontVariant.BodyBold}>
                                {t(
                                    'settings.mirror-selfview',
                                    'Mirror self-view',
                                )}
                            </Text>
                        }
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={setPreviewMirror}
                                labelModifier="hidden"
                                label={t(
                                    'settings.mirror-selfview',
                                    'Mirror self-view',
                                )}
                                name="mirrorSelfview"
                                checked={previewMirror}
                            />
                        </div>
                    </SettingsActionsRow>
                </SelfViewSettings>
                {deviceList}
                <List spacing="none" className="mt-1">
                    <OutputAudioTester
                        sinkId={audioOutput.preview?.deviceId ?? ''}
                    />
                </List>
            </ThemeProvider>
        </SettingsPanel>
    );
};
